import React from 'react';
import { ReportData } from '../../types/report.types';

interface FindingsSummaryEditorProps {
  reportData: ReportData;
  onTableChange: (field: string, value: number) => void;
}

const FindingsSummaryEditor: React.FC<FindingsSummaryEditorProps> = ({ reportData }) => {
  const severityColors = {
    Critical: 'bg-red-50 border-red-200 text-red-700',
    High: 'bg-orange-50 border-orange-200 text-orange-700',
    Medium: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    Low: 'bg-blue-50 border-blue-200 text-blue-700',
    Info: 'bg-slate-50 border-slate-200 text-slate-700'
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">Findings Summary</h3>
          <p className="text-xs text-gray-600">
            View summary counts across severity levels (automatically updated from findings)
          </p>
        </div>
        <div className="p-4">
          {/* Severity Counts */}
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(reportData.open_close_counts_by_severity || {}).map(([severity, counts]) => (
              <div
                key={severity}
                className={`rounded-lg p-4 border ${severityColors[severity as keyof typeof severityColors] || 'bg-slate-50 border-slate-200 text-slate-700'}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${severity === 'Critical' ? 'bg-red-500' :
                      severity === 'High' ? 'bg-orange-500' :
                        severity === 'Medium' ? 'bg-yellow-500' :
                          'bg-blue-500'
                    }`} />
                  <h4 className="font-medium text-sm">{severity}</h4>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Open', key: 'Open' },
                    { label: 'Closed', key: 'Closed' },
                    { label: 'Business Accepts Risk', key: 'BusinessAcceptsRisk' },
                    { label: 'Business Non Actionable', key: 'BusinessNonActionable' },
                    { label: 'Total', key: 'Total' }
                  ].map(({ label, key }) => {
                    const value = (counts as any)[key] || 0;
                    const isTotal = key === 'Total';
                    return (
                      <div key={key} className={`flex items-center justify-between ${isTotal ? 'pt-2 border-t border-gray-200 mt-2' : ''}`}>
                        <span className={`text-sm font-medium min-w-[150px] ${isTotal ? 'font-bold' : ''}`}>{label}:</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={value}
                            disabled
                            readOnly
                            className={`w-16 border border-gray-300 rounded-md px-2 py-1 text-center text-sm bg-gray-100 text-gray-700 cursor-not-allowed ${isTotal ? 'font-bold bg-gray-200' : ''}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Total Counts */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            {[
              {
                label: 'Total Open',
                value: reportData.total_open || 0,
                field: 'total_open',
                icon: (
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              },
              {
                label: 'Total Closed',
                value: reportData.total_closed || 0,
                field: 'total_closed',
                icon: (
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )
              },
              {
                label: 'Total Findings',
                value: reportData.total_findings || 0,
                field: 'total_findings',
                icon: (
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                )
              }
            ].map(({ label, value, field, icon }) => (
              <div key={field} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  {icon}
                  <h4 className="font-medium text-sm text-gray-800">{label}</h4>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="number"
                    value={value}
                    disabled
                    readOnly
                    className="w-20 border border-gray-300 rounded-md px-2 py-1 text-center text-sm font-bold bg-gray-100 text-gray-700 cursor-not-allowed"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Info Box */}
          <div className="mt-6 bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-blue-800 font-medium mb-1">Automated Findings Summary</p>
                <p className="text-sm text-blue-700">
                  Table numbers in this summary are greyed out and read-only. They are calculated automatically from your findings' status and severity levels to guarantee complete consistency. To change status or severity counts, edit your findings in the section editors or Finding Config table.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindingsSummaryEditor; 